import { useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody
} from '@chakra-ui/react';
import {
  faArrowsRotate,
  faFile,
  faSpinner
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@morressier/alembic';
import ReviewSummaryAccordion from '../../decisionModals/ReviewSummaries';
import { fetchActiveRevision } from 'src/api/queries';
import { IRevisionRound, IRoleType } from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import string from 'src/utils/string';
import { Overlay } from 'src/components/Overlay';
import AdjudicatorReviewSummary from '../../decisionModals/AdjudicatorReviewSummary';
import { formatDeadlineDate } from 'src/utils/date';
import FileViewerButtonText from '../../FileViewerButtonText';

interface Props {
  submissionId: string;
  submissionContextId?: string;
  onClose: () => void;
  deadline?: Date;
  role: IRoleType;
  defaultData?: IRevisionRound;
}

export const EditorialRevisionContextModal = ({
  role,
  onClose,
  deadline,
  submissionId,
  defaultData,
  submissionContextId
}: Props) => {
  const { data: activeRevision, isLoading } = useQuery({
    queryKey: ['active-revision', submissionId],
    queryFn: () =>
      !defaultData ? fetchActiveRevision(submissionId, role) : undefined
  });

  const data = defaultData || activeRevision;
  const initialRef = useRef<HTMLDivElement | null>(null);

  if (isLoading)
    return (
      <Overlay>
        <Flex justifyContent="center" alignItems="center" marginTop={'50px'}>
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        </Flex>
      </Overlay>
    );

  if (!data) return null;

  return (
    <Modal isOpen onClose={onClose} size="3xl" initialFocusRef={initialRef}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody padding={0}>
          <Flex
            ref={initialRef}
            padding={8}
            gap={6}
            flexDirection="column"
            maxHeight="80vh"
            overflow="auto"
          >
            <Box>
              <FontAwesomeIcon fontSize={32} icon={faArrowsRotate} />
            </Box>

            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Revision Context</Text>
            </Flex>
            {data.severity && (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">Type of Revision</Text>
                <Badge size="context" variant="attention" width={'fit-content'}>
                  <Flex gap={2} alignItems={'center'}>
                    <FontAwesomeIcon icon={faArrowsRotate} />
                    <span>
                      {string.capitalize(`${data.severity} revision`)}
                    </span>
                  </Flex>
                </Badge>
              </Flex>
            )}

            {deadline && (
              <Flex gap={2} flexDirection="column">
                <Text textStyle="headingItem">Deadline</Text>
                <Text textStyle="textBody">{formatDeadlineDate(deadline)}</Text>
              </Flex>
            )}

            <Flex gap={2} flexDirection="column">
              <Flex alignItems="center" gap={2}>
                <Text textStyle="headingItem">Response to Author </Text>
              </Flex>
              <Text textStyle="textBody">{data.editorialComment}</Text>
              <Flex flexDirection="column" gap={0}>
                {' '}
                {data.editorialAttachments &&
                  data.editorialAttachments.map(file => (
                    <Flex
                      justifyContent="space-between"
                      key={file?.referenceName}
                    >
                      <Flex gap={2} alignItems="center">
                        <FontAwesomeIcon icon={faFile} />
                        <Text textStyle="textBody">{file?.referenceName}</Text>
                        <FileViewerButtonText
                          submissionContextId={submissionContextId}
                          file={file}
                        >
                          View
                        </FileViewerButtonText>
                      </Flex>
                    </Flex>
                  ))}
              </Flex>
            </Flex>
            <Flex direction="column">
              <ReviewSummaryAccordion
                reviews={data.reviewerOriginalFeedback ?? []}
              />
              <AdjudicatorReviewSummary
                adjudicatorReview={data.adjudicatorOriginalFeedback ?? []}
              />
            </Flex>
            <Flex
              gap={2}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf="stretch"
            >
              <Button
                onClick={onClose}
                variant="primary"
                type="submit"
                borderRadius="small"
              >
                Close
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
